import React from 'react';

import { useCms } from '~/components/hooks/useCms';
import { ADMIN_ROUTES } from '~/routesConstants';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, isLegacyOrgFeatureEnabled } from '~/Utils';

import ConfigurationScreenWithTabs from '../ConfigurationScreenWithTabs';
import { useN8nConfiguration } from '../N8nConfiguration/hooks/useN8nConfiguration';
import { useSysconfig } from '../SystemConfigurationScreen';

import AssignmentsTab from './Assignments/AssignmentsTab';
import NotificationsConfigurationTool from './Notifications/NotificationsConfigurationTool';
import AutomaticCommunications from './Communications';
import AutomaticExposures from './Exposures';
import FundAccounts from './FundAccounts';
import Guardrails from './Guardrails';
import SlaEngine from './SlaEngine';
import WorkflowEngine from './WorkflowEngine';

const AUTOMATIC_RULES_CONFIGURATION_SUBTITLE =
  'Set up custom rules in no-code engines which automatically trigger actions to automate processes within the claims management system.';

const AutomaticRulesConfiguration = () => {
  const { organization } = useSysconfig();
  const { isN8nEnabled } = useN8nConfiguration();
  // The non-impersonated organization: `is_super_organization` is the established gate for an
  // internal-only sysconfig surface (the Metabase and Dyte tabs read the same field). Fund
  // Accounts is FiveSigma-internal for its first rollout phase, on top of the org's own flag.
  const { userOrganization: nonImpersonatedOrganization } = useCms();

  const tabs = [
    {
      label: 'Exposures',
      url: ADMIN_ROUTES.AUTOMATION.EXPOSURES,
      component: AutomaticExposures,
      isEnabled: isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.AUTOMATIC_EXPOSURES_RULES),
    },
    {
      label: 'Communications',
      url: ADMIN_ROUTES.AUTOMATION.COMMUNICATIONS,
      component: AutomaticCommunications,
      isEnabled: isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL),
    },
    {
      label: 'Assignments',
      url: ADMIN_ROUTES.AUTOMATION.ASSIGNMENTS,
      component: AssignmentsTab,
      isEnabled: isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.ASSIGNMENTS_CONFIGURATION),
    },
    {
      label: 'Notifications',
      url: ADMIN_ROUTES.AUTOMATION.NOTIFICATIONS,
      component: NotificationsConfigurationTool,
      isEnabled: true,
    },
    {
      label: 'Workflows',
      url: ADMIN_ROUTES.AUTOMATION.WORKFLOWS,
      component: WorkflowEngine,
      isEnabled: isN8nEnabled,
    },
    {
      label: 'Guardrails',
      url: ADMIN_ROUTES.AUTOMATION.GUARDRAILS,
      component: Guardrails,
      // The backend does not evaluate guardrails for a hospitality-only organization.
      isEnabled: !isLegacyOrgFeatureEnabled(organization, LEGACY_ORG_FEATURES.HOSPITALITY_NO_GUARDRAILS),
    },
    {
      label: 'Fund Accounts',
      url: ADMIN_ROUTES.AUTOMATION.FUND_ACCOUNTS,
      component: FundAccounts,
      // `isEnabled` is declared `bool.isRequired` and the tab list is filtered on it, so the
      // coercion matters: a missing `userOrganization` would otherwise pass `undefined` through.
      isEnabled:
        isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.FUND_ACCOUNT_ENGINE) &&
        Boolean(nonImpersonatedOrganization?.is_super_organization),
    },
    {
      label: 'SLA Engine',
      url: ADMIN_ROUTES.AUTOMATION.SLA_ENGINE,
      component: SlaEngine,
      isEnabled: isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.SLA_ENGINE),
    },
  ];

  return (
    <ConfigurationScreenWithTabs
      tabs={tabs}
      title="AUTOMATIC RULES CONFIGURATION"
      subtitle={AUTOMATIC_RULES_CONFIGURATION_SUBTITLE}
      operationTabName="Automatic Rules Configuration"
    />
  );
};

export default AutomaticRulesConfiguration;
export { AUTOMATIC_RULES_CONFIGURATION_SUBTITLE };
